import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import {
  buildChartsModel,
  emptyChartsModel,
  selectTagsAnswer
} from '@aytm/app/stats/page/question-views/oe/lib/chartsModel'
import { calcTableProps } from '@aytm/app/stats/page/question-views/oe/lib/sentiments-table-utils'

import { isMapOrNone } from '@aytm/app/stats/page/components/lib/utils'
import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'

import Chart from '@aytm/app/stats/page/question-views/oe/components/chart/Chart'
import Table from '@aytm/app/stats/components/data-views/table/components/Table'
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'

// The sentiments / tags surface: one chart over one items list, both built from the same
// re-keyed answer. block.coffee resolves the tag name list -- that needs page state -- and
// hands it in; everything derived from it is computed here rather than by the block, so the
// chart and the table cannot disagree about what a row means.
const ChartsView = ({ components, answer, chartHeight, chartsModelProps, chartProps, tableProps }) => {
  // Every resolved name gets a consecutive a{n} slot even when the payload carries no
  // matching tag, so a name keeps its position across payloads.
  const { kind, viewMode, answer: modelAnswer, config, keys, codebook } = chartsModelProps || {}

  const chartsModel = useMemo(() => {
    if ('tags' !== kind) return emptyChartsModel()

    return buildChartsModel({
      viewMode,
      answer: selectTagsAnswer(viewMode, modelAnswer, config),
      keys,
      codebook
    })
  }, [kind, viewMode, modelAnswer, config, keys, codebook])

  // the payload sentinel is the raw answer, so the view decides its own loading state
  const isLoading = 'loading' === answer

  return (
    <>
      {isComponentOn(components, 'chart') &&
        !isMapOrNone(chartProps?.questionChartMode, chartProps?.globalChartMode) && (
          <ChartPlaceholder height={chartHeight} isLoading={isLoading}>
            <Chart answer={chartsModel.answer} answerVariants={chartsModel.answerVariants} {...chartProps} />
          </ChartPlaceholder>
        )}
      {isComponentOn(components, 'table') && (
        <Table
          {...calcTableProps({
            answer: chartsModel.answer,
            answerVariants: chartsModel.answerVariants,
            ...(tableProps || {})
          })}
        />
      )}
    </>
  )
}

ChartsView.displayName = 'OeChartsView'

ChartsView.propTypes = {
  components: PropTypes.object,
  answer: answerPropType,
  chartHeight: PropTypes.number,
  chartsModelProps: PropTypes.object,
  chartProps: PropTypes.object,
  tableProps: PropTypes.object
}

export default ChartsView
